import { PromptStyle } from '../enums/prompt-style.ts'
import { DisplayAnimationBehavior } from './display-animation-behavior.ts'
import { RelativeDate } from './relative-date.ts'
import { RelativeDateDisplayMetric } from '../enums/relative-date-display-metric.ts'
import { FrequencyCap } from '@core/types/frequency-cap.ts'

/** @internal */
export type PromptBehaviors = {
    readonly is_auto_show?: boolean
    /** @deprecated */
    readonly cookie_length_seconds?: number
    readonly post_dismiss_redisplay: { enabled: false } | { enabled: true; fcap: Omit<FrequencyCap, 'occurrences'> }
}

/** @internal */
export type PromptConditions = {
    // Optional: the platform persists `{}` for a prompt whose visitor condition was cleared,
    // so this member is genuinely absent on live payloads and every read must be guarded.
    readonly visitor?: PromptVisitorConditions
}

type AbstractPrompt = {
    readonly id: number
    readonly domain: number
    readonly name: string | null
    readonly style: PromptStyle
    readonly weight: number
    readonly conditions: PromptConditions | null
    readonly behavior: PromptBehaviors
}

/** @internal */
export enum PromptPosition {
    TOP = 'top',
    BOTTOM = 'bottom',
}

/** @internal */
export type PromptPlacementPositionConfig = {
    desktop: PromptPosition
    mobile: PromptPosition
}

/**
 * Server-controlled text for a single themed string: a title, a subtitle, or a button label. The
 * DOM renderer writes `text` via `textContent` only, never through any HTML-parsing assignment, so
 * a malicious payload can make the string itself look like markup but can never have it parsed as
 * markup. `text_color` is optional and, like every other colour in {@link PromptTheme}, must pass
 * {@link isSafeCssColor} before the renderer applies it — an unset or rejected value falls back to
 * the stylesheet's own default.
 *
 * @internal
 */
export type PromptThemeText = {
    readonly text: string
    readonly text_color?: string
}

/**
 * A themed button's label plus its background colour. Both colours on a button (`text_color`
 * inherited from {@link PromptThemeText} and `background_color` here) are validated independently
 * before use — one failing validation does not block the other.
 *
 * @internal
 */
export type PromptThemeButton = PromptThemeText & {
    readonly background_color?: string
}

/**
 * Structured slide prompt theme data, carried on the payload in place of the server-rendered
 * markup this story removes. Every field here is attacker-reachable — it arrives from the CDN —
 * so the DOM renderer treats presence in this type as "shaped like the field", never as "safe to
 * use unvalidated": colours are checked against {@link isSafeCssColor}, `image` against
 * {@link isSafeImageUrl}, and `layout` / `position` values are matched against a fixed set of
 * recognised strings before they ever become a class name.
 *
 * A theme missing `title`, `subtitle`, `allow_button`, or `dismiss_button` (or carrying one with
 * empty `text`) has nothing worth rendering; the renderer treats that as "no usable theme" and
 * builds nothing rather than guessing at placeholder copy.
 *
 * @internal
 */
export type PromptTheme = {
    readonly position: PromptPlacementPositionConfig
    /**
     * Expected to be `'normal'` or `'stacked'` (mirroring the server's `SlidePromptLayout` enum),
     * but read from the payload as an unconstrained string — any other value, including one an
     * older or compromised payload never meant to send, simply earns no `l-*` class rather than
     * being rejected outright.
     */
    readonly layout?: string
    readonly background_color?: string
    readonly title?: PromptThemeText
    readonly subtitle?: PromptThemeText
    /**
     * `undefined` (field absent) and `null` (explicitly cleared) are both "no image" — the
     * `.pushly-prompt-image` node is still built in either case, per the structural contract, and
     * stays hidden because the renderer never sets the image custom properties.
     */
    readonly image?: string | null
    readonly allow_button?: PromptThemeButton
    readonly dismiss_button?: PromptThemeButton
}

/** @internal */
type HtmlPromptBehaviors = PromptBehaviors & {
    readonly display_animations?: DisplayAnimationBehavior
}

/** @internal */
export type PromptDisplayConditions = {
    readonly mobile: 'enabled' | 'disabled' | 'pass'
    readonly desktop: 'enabled' | 'disabled' | 'pass'
}

/** @internal */
export type PromptPageConditions = {
    readonly page_urls: string[]
    readonly excluded_page_urls: string[]
    readonly keywords: string[]
}

/** @internal */
export type NonHtmlPrompt = AbstractPrompt & {
    readonly style: PromptStyle.NATIVE | PromptStyle.PUBLISHER_CUSTOM
}

/** @internal */
export type HtmlPrompt = AbstractPrompt & {
    readonly style: PromptStyle.SLIDE
    readonly template_html: string
    readonly theme: PromptTheme
    readonly behavior: HtmlPromptBehaviors
}

/** @internal */
export type Prompt = NonHtmlPrompt | HtmlPrompt

/** @internal */
export type PromptGroupConditions = {
    readonly display?: PromptDisplayConditions
    readonly page?: PromptPageConditions
}

/** @internal */
export type PromptVisitorConditions =
    | {
          readonly time_on_page_seconds: number
      }
    | {
          readonly session_page_views: number
      }
    | {
          readonly session_time_on_site_seconds: number
      }
    | {
          readonly scroll_depth_pct: number
      }

/** @internal */
export type PromptGroupBehavior = {
    readonly display_to_pct: number
}

/** @internal */
export type PromptGroup = {
    readonly id: number
    readonly domain: number
    readonly name: string
    readonly priority: number
    readonly conditions: PromptGroupConditions | null
    readonly behavior: PromptGroupBehavior
    readonly prompts: Prompt[]
}

/** @internal */
export enum PromptConditionType {
    SESSION_START = 'SESSION_START',
    SESSION_TIME_ON_SITE = 'SESSION_TIME_ON_SITE',
    SESSION_TIME_ON_PAGE = 'SESSION_TIME_ON_PAGE',
    SESSION_PAGE_VIEWS = 'SESSION_PAGE_VIEWS',
    SCROLL_DEPTH = 'SCROLL_DEPTH',
}

/** @internal */
export type PromptSessionStartCondition = {
    type: PromptConditionType.SESSION_START
}

/** @internal */
export type PromptSessionTimeIntervalCondition = {
    type: PromptConditionType.SESSION_TIME_ON_SITE | PromptConditionType.SESSION_TIME_ON_PAGE
    timeInterval: RelativeDate
}

/** @internal */
export type PromptPageViewsCondition = {
    type: PromptConditionType.SESSION_PAGE_VIEWS
    minPageViews: number
}

/** @internal */
export type PromptScrollDepthCondition = {
    type: PromptConditionType.SCROLL_DEPTH
    minScrollDepthPct: number
}

/** @internal */
export type PromptCondition =
    | PromptSessionStartCondition
    | PromptSessionTimeIntervalCondition
    | PromptPageViewsCondition
    | PromptScrollDepthCondition

// region Prompt Helpers

/** @internal */
export function getPromptCondition(conditions: PromptConditions | null): PromptCondition {
    // `visitor` may be absent, null, or a non-object on stored payloads. Reading through it
    // unguarded throws, and that exception propagates out of the display evaluation and
    // suppresses the prompt entirely, so anything unusable degrades to SESSION_START -
    // the same behavior as a prompt that carries no conditions at all.
    const visitor = conditions?.visitor
    if (visitor && typeof visitor === 'object') {
        if ('session_page_views' in visitor) {
            return {
                type: PromptConditionType.SESSION_PAGE_VIEWS,
                minPageViews: visitor.session_page_views,
            }
        }
        if ('session_time_on_site_seconds' in visitor || 'time_on_page_seconds' in visitor) {
            return {
                type:
                    'session_time_on_site_seconds' in visitor
                        ? PromptConditionType.SESSION_TIME_ON_SITE
                        : PromptConditionType.SESSION_TIME_ON_PAGE,
                timeInterval: {
                    intervalSeconds:
                        'session_time_on_site_seconds' in visitor
                            ? visitor.session_time_on_site_seconds
                            : visitor.time_on_page_seconds,
                    displayMetric: RelativeDateDisplayMetric.SECONDS,
                },
            }
        }
        if (
            'scroll_depth_pct' in visitor &&
            Number.isInteger(visitor.scroll_depth_pct) &&
            visitor.scroll_depth_pct >= 1 &&
            visitor.scroll_depth_pct <= 100
        ) {
            return {
                type: PromptConditionType.SCROLL_DEPTH,
                minScrollDepthPct: visitor.scroll_depth_pct,
            }
        }
    }

    return {
        type: PromptConditionType.SESSION_START,
    }
}

/** @internal */
export function isSessionStartPromptCondition(condition: PromptCondition): condition is PromptSessionStartCondition {
    return condition.type === PromptConditionType.SESSION_START
}

/** @internal */
export function isSessionTimeIntervalPromptCondition(
    condition: PromptCondition,
): condition is PromptSessionTimeIntervalCondition {
    return [PromptConditionType.SESSION_TIME_ON_SITE, PromptConditionType.SESSION_TIME_ON_PAGE].includes(condition.type)
}

/** @internal */
export function isPageViewsPromptCondition(condition: PromptCondition): condition is PromptPageViewsCondition {
    return condition.type === PromptConditionType.SESSION_PAGE_VIEWS
}

/** @internal */
export function isScrollDepthPromptCondition(condition: PromptCondition): condition is PromptScrollDepthCondition {
    return condition.type === PromptConditionType.SCROLL_DEPTH
}

/** @internal */
export function isAutoShowPrompt(prompt: Prompt): boolean {
    return prompt.style !== PromptStyle.PUBLISHER_CUSTOM && (prompt.behavior.is_auto_show ?? true)
}

/** @internal */
export function isSessionStartPrompt(prompt: Prompt): boolean {
    return (
        prompt.style !== PromptStyle.PUBLISHER_CUSTOM &&
        isSessionStartPromptCondition(getPromptCondition(prompt.conditions))
    )
}

/** @internal */
export function isHtmlPrompt(prompt: Prompt): prompt is HtmlPrompt {
    return prompt.style === PromptStyle.SLIDE
}

/** @internal */
export function promptGroupsUseScrollDepthTrigger(groups: PromptGroup[]): boolean {
    return groups.some((group) =>
        group.prompts.some(
            (prompt) =>
                prompt.style !== PromptStyle.PUBLISHER_CUSTOM &&
                isScrollDepthPromptCondition(getPromptCondition(prompt.conditions)),
        ),
    )
}

// endregion
